import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  SxProps,
  Theme,
} from 'ui/mui';
import { ReactNode } from 'react';
import { darkModeColors } from 'ui/theme/theme';
import { pxToRem } from 'ui/utils/text';
import { FooterLink } from './FooterLink';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';

export type FooterListItem = {
  text?: string;
  href?: string;
  linkText?: string;
  component?: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  external?: boolean;
};

export type FooterList = {
  title: string;
  items: FooterListItem[];
};

type FooterListItemsProps = {
  lists: (FooterList | undefined)[];
  sx?: SxProps<Theme>;
};

export const FooterListItems = ({ lists, sx }: FooterListItemsProps) => {
  if (lists.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 8,
        flexDirection: { xs: 'column', lg: 'row' },
        ...sx,
      }}
    >
      {lists.map((list, index) => (
        <Box key={index}>
          <Typography variant='h4' component='div'>
            {list!.title}
          </Typography>
          <List
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '& .MuiListItem-root': { p: 0, gap: 1 },
            }}
          >
            {list!.items.map((item, itemIndex) => {
              const itemIcon = (
                <Box
                  sx={{
                    color: '#FF6932',
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: item.icon ? pxToRem(24) : pxToRem(16),
                    height: item.icon ? pxToRem(24) : pxToRem(16),
                    '& > svg': {
                      fontSize: item.icon ? pxToRem(24) : pxToRem(10),
                    },
                  }}
                >
                  {item.icon ?? <CircleRoundedIcon />}
                </Box>
              );
              return (
                <ListItem key={itemIndex}>
                  {item.component ? (
                    item.component
                  ) : item.onClick ? (
                    <Box
                      onClick={item.onClick}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: darkModeColors.text.primary,
                      }}
                    >
                      {itemIcon}
                      {item.text && (
                        <Typography variant='body1' component='span'>
                          {item.text}{' '}
                        </Typography>
                      )}
                      <Typography
                        variant='body1'
                        component='span'
                        sx={{
                          textDecoration: 'underline',
                          '&:hover': {
                            textDecorationThickness: '2px',
                          },
                        }}
                      >
                        {item.linkText || item.text}
                      </Typography>
                    </Box>
                  ) : item.href && item.linkText && item.text ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {itemIcon}
                      <Box>
                        <Typography variant='body1' component='span'>
                          {item.text}{' '}
                        </Typography>
                        <FooterLink href={item.href} external={item.external}>
                          {item.linkText}
                        </FooterLink>
                      </Box>
                    </Box>
                  ) : item.href ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {itemIcon}
                      <FooterLink href={item.href} external={item.external}>
                        {item.linkText || item.text}
                      </FooterLink>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {itemIcon}
                      <ListItemText primary={item.text} />
                    </Box>
                  )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};
